import React, { useEffect, useMemo, useState } from 'react';
import { Box, FormControlLabel } from '@mui/material';
import clsx from 'clsx';
import {
  btnSize,
  btnType,
  Button,
  fontWeight,
  Heading,
  headingLevel,
  Input,
  inputSize,
  inputType,
  Select,
  selectType,
  Textarea,
  TextError,
  Toggle,
} from '../../../../../../packages';
import { useDispatch, useSelector } from 'react-redux';
import { profileActionCreators } from '../../../../../../redux/modules/profile';
import { getProfile } from '../../../../../../redux/modules/profile/selectors';
import { getDomainElements } from '../../../../../../services/member/member.service';
import SelectLevel from '../../select-level';
import { useStyles } from './MedicationForm.styles';
import { MEDICATION_DOSES, MEDICATION_FREQUENCIES } from '../../../../../../constants/CommonConstants';
import { generateSig, searchExternalMedications } from '../../../../../../services/schedule/schedule.service';
import { showSnackbar } from '../../../../../../redux/modules/snackbar';
import debounce from 'debounce';

const frequencies = [{ display: 'Select Frequency', abbr: '' }, ...MEDICATION_FREQUENCIES].map(frequency => ({
  label: frequency.display,
  title: frequency.display,
  value: frequency.abbr,
}));
const doses = MEDICATION_DOSES.map(dose => ({ label: dose, value: dose }));

const MedicationForm = ({ form, error, patientDiagnoses, options, medication }) => {
  const dispatch = useDispatch();

  const [importanceLevels, setImportanceLevels] = useState([]);
  const [sigLoading, setSigLoading] = useState(false);
  const [externalMedications, setExternalMedications] = useState([]);
  const diagnosesOptions = patientDiagnoses?.map(diagnosis => diagnosis.taxonomyName) || [];
  const { providers } = useSelector(getProfile);
  const providerListOptions = providers
    .filter(provider => provider.active)
    .filter(provider => provider.designation === 'Nurse Practitioner' || provider.designation === 'Physician')
    .map(provider => ({
      label: provider.fullName,
      value: provider.providerId,
    }));

  useEffect(() => {
    dispatch(
      profileActionCreators.fetchProviders({
        searchQuery: '',
        pageNumber: 1,
        pageSize: 1000,
        orderBy: 'asc',
        sortBy: '',
      }),
    );
  }, []);

  useEffect(() => {
    if (medication) {
      if (medication?.domainElementId) {
        searchForExternalMedications('', medication.domainElementId).then(() => {
          console.log('Initial List fetched');
        });
        const tempImportanceLevels = [];
        getDomainElements({ domainTypeId: medication?.domainElementId })
          .then(res => {
            if (res.status === 200) {
              Object.keys(res?.data?.metaData?.metaDataSpec?.importanceLevels).forEach(key => {
                if (res?.data?.metaData?.metaDataSpec?.importanceLevels[key]) {
                  tempImportanceLevels.push({ value: key, label: key });
                }
              });
              setImportanceLevels(tempImportanceLevels);
            }
          })
          .catch(err => {
            console.log({ err });
          });
      } else {
        const selectedDiagnosis = options?.filter(option => option?.groupId === medication?.groupId);
        const selectedRelatedElement = selectedDiagnosis[0]?.relatedElements?.filter(
          element => element.Id === medication.item.value,
        );

        const tempImportanceLevels = [];
        Object.keys(selectedRelatedElement[0]?.metaData?.metaDataSpec?.importanceLevels).forEach(key => {
          if (selectedRelatedElement[0]?.metaData?.metaDataSpec?.importanceLevels[key]) {
            tempImportanceLevels.push({ value: key, label: key });
          }
        });
        setImportanceLevels(tempImportanceLevels);
      }
    }
  }, []);

  const classes = useStyles();
  const { values, touched, errors, setFieldValue, handleChange, ...rest } = form;

  const setTouched = name => async () => {
    await rest.setTouched({ ...touched, [name]: true });
  };

  const renderLabel = (str, className = '') => (
    <Heading level={headingLevel.S} className={clsx(classes.label, className)} weight={fontWeight.BOLD}>
      {str}
    </Heading>
  );

  useEffect(() => {
    if (values?.status === '') {
      setFieldValue('status', 'INACTIVE');
    }
  }, [values]);

  useEffect(() => {
    const selectedFrequency = frequencies.find(f => f.value === values.frequency?.value);
    if (selectedFrequency?.value && values.quantity && values.duration) {
      const frequencyQuantity = MEDICATION_FREQUENCIES.find(
        f => f.abbr === selectedFrequency.value,
      )?.quantity;
      if (frequencyQuantity !== null && frequencyQuantity !== undefined) {
        const calculatedAmount = Math.ceil(values.quantity * frequencyQuantity * values.duration);
        setFieldValue('dispenseAmount', calculatedAmount);
      } else {
        setFieldValue('dispenseAmount', '');
      }
    }
  }, [values.frequency, values.quantity, values.duration]);

  // TODO: Add validation for refills
  const isSigDisabled =
    !values.dose ||
    values.dose === '' ||
    !values.frequency ||
    values.frequency === '' ||
    values.frequency?.value === '' ||
    !values.quantity ||
    values.quantity === '' ||
    !values.duration ||
    values.duration === '';

  const handleSigGeneration = async () => {
    setSigLoading(true);
    try {
      const request = {
        quantity: values.quantity,
        dose: values.dose,
        frequency: values.frequency.value ?? values.frequency,
        duration: values.duration,
      };
      const { data } = await generateSig(request);
      if (data?.data?.sig) {
        setFieldValue('sig', data.data.sig);
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: 'Failed to generate sig',
          }),
        );
      }
    } catch (error) {
      console.warn(error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to generate sig',
        }),
      );
    }
    setSigLoading(false);
  };

  const searchForExternalMedications = async (searchQuery, selectedMedication) => {
    const response = await searchExternalMedications(searchQuery, selectedMedication);
    if (response.data) {
      setExternalMedications(response.data);
    } else {
      console.error('Failed to search for external medications');
    }
  };

  const debounceSearch = async searchQuery => {
    void searchForExternalMedications(searchQuery, '');
  };

  const drugSearchOptions = externalMedications.map(med => ({
    label: med.drug,
    value: med.id,
    drugDefaults: med.drugDefaults, // Include drugDefaults for auto-population
  }));
  const searchDelayed = useMemo(() => debounce(debounceSearch, 1000), [debounceSearch]);

  /**
   * Auto-populate form fields with drug defaults when a medication is selected
   */
  const populateDefaultValues = selectedMedication => {
    if (!selectedMedication?.drugDefaults) return;

    const { drugDefaults } = selectedMedication;

    // Map the default values to form fields
    if (drugDefaults.quantity !== undefined && drugDefaults.quantity !== null) {
      setFieldValue('quantity', drugDefaults.quantity);
    }

    if (drugDefaults.dose) {
      // Find matching dose from predefined options
      const matchingDose = doses.find(dose => dose.value.toLowerCase() === drugDefaults.dose.toLowerCase());
      if (matchingDose) {
        setFieldValue('dose', matchingDose.value);
      }
    }

    if (drugDefaults.frequency) {
      // Find the matching frequency object from the frequencies array
      const matchingFrequency = frequencies.find(freq => freq.value === drugDefaults.frequency);
      if (matchingFrequency) {
        setFieldValue('frequency', matchingFrequency);
      }
    }

    if (drugDefaults.duration !== undefined && drugDefaults.duration !== null) {
      setFieldValue('duration', drugDefaults.duration);
    }

    if (drugDefaults.refills !== undefined && drugDefaults.refills !== null) {
      // Ensure refills is a number and within valid range (0-12)
      const refillsValue = Number(drugDefaults.refills);
      if (refillsValue >= 0 && refillsValue <= 12 && Number.isInteger(refillsValue)) {
        setFieldValue('refills', refillsValue);
      }
    }

    if (drugDefaults.sig) {
      setFieldValue('sig', drugDefaults.sig);
    }

    if (drugDefaults.pharmacyNotes) {
      setFieldValue('pharmacyNotes', drugDefaults.pharmacyNotes);
    }

    if (drugDefaults.note) {
      setFieldValue('notes', drugDefaults.note);
    }
  };

  return (
    <Box className={classes.formContent}>
      {error && (
        <Box display="flex" justifyContent="center">
          <TextError errorMsg={error} />
        </Box>
      )}
      <Box className={classes.section}>
        {renderLabel('Drug')}
        <Input
          fullWidth
          variant={inputType.AUTOCOMPLETE}
          filterSelectedOptions={false}
          name="externalRx"
          options={drugSearchOptions}
          getOptionLabel={option => option?.label ?? option}
          getOptionDisabled={option => !option || option?.value === '' || option === ''}
          isOptionEqualToValue={(option, value) => {
            return option.value === value || option.value === value.value;
          }}
          value={values.externalRx}
          size={inputSize.M}
          placeholder="Search for a drug"
          onChange={(event, value) => {
            setFieldValue('externalRx', value);
            // Auto-populate form fields with drug defaults if available
            if (value?.drugDefaults) {
              populateDefaultValues(value);
            }
          }}
          onInputChange={(event, ev2) => {
            if (!event) {
              return;
            }
            if (
              event.code === 'Enter' ||
              event.code === 'NumpadEnter' ||
              event.type === 'click' ||
              event.type === 'blur'
            ) {
              return;
            }
            if (event.target && event.target.value) {
              searchDelayed(event.target.value);
            }
          }}
          onBlur={setTouched('externalRx')}
        />
        <TextError errorMsg={touched.externalRx ? errors.externalRx?.toString() : null} />
      </Box>
      <Box className={classes.section}>
        {renderLabel('Importance level')}
        <SelectLevel
          name="status"
          value={values.status}
          isLifeEvent
          options={importanceLevels}
          onChange={event => setFieldValue('status', event.target.value)}
        />
      </Box>
      <Box display="flex" flexDirection="row" gap="8px">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flex: 0.3,
          }}
        >
          {renderLabel('Quantity')}
          <Input
            fullWidth
            name="quantity"
            value={values.quantity}
            size={inputSize.M}
            placeholder="Enter Quantity"
            onChange={event => {
              if (event.target.value === '') {
                return onChangeHandler('quantity', '');
              } else {
                if (Number(event.target.value) > 0) {
                  return setFieldValue('quantity', Number(event.target.value));
                }
              }
            }}
            type="number"
            onBlur={setTouched('quantity')}
          />
          <TextError errorMsg={touched.quantity ? errors.quantity?.toString() : null} />
        </Box>
        <Box className={classes.section}>
          {renderLabel('Dose')}
          <Select
            name="dose"
            value={values.dose}
            variant={selectType.SECONDARY}
            options={doses?.sort((a, b) => a.label.localeCompare(b.label))}
            emptyText="Select item"
            displayEmpty
            onChange={event => setFieldValue('dose', event.target.value)}
          />
          <TextError errorMsg={touched.dose ? errors.dose?.toString() : null} />
        </Box>
      </Box>
      <Box display="flex" flexDirection="row" gap="8px">
        <Box className={classes.section}>
          {renderLabel('Frequency')}
          <Input
            fullWidth
            variant={inputType.AUTOCOMPLETE}
            filterSelectedOptions={false}
            name="frequency"
            options={frequencies?.sort((a, b) => a.label.localeCompare(b.label))}
            getOptionLabel={option => option?.label ?? option}
            getOptionDisabled={option => !option || option?.value === '' || option === ''}
            isOptionEqualToValue={(option, value) => {
              return option.value === value || option.value === value.value;
            }}
            value={values.frequency}
            size={inputSize.M}
            placeholder="Enter or Select Frequency"
            onChange={(event, value) => {
              setFieldValue('frequency', value);
            }}
            onBlur={setTouched('frequency')}
          />
          <TextError errorMsg={touched.frequency ? errors.frequency?.toString() : null} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flex: 0.4,
          }}
        >
          {renderLabel('Duration')}
          <Input
            fullWidth
            name="duration"
            value={values.duration}
            size={inputSize.M}
            endAdornment="Days"
            placeholder="Enter Duration"
            onChange={event => {
              if (event.target.value === '') {
                return onChangeHandler('duration', '');
              } else {
                if (Number(event.target.value) > 0) {
                  return setFieldValue('duration', Math.trunc(Number(event.target.value)));
                }
              }
            }}
            type="number"
            onBlur={setTouched('duration')}
          />
          <TextError errorMsg={touched.duration ? errors.duration?.toString() : null} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flex: 0.5,
          }}
        >
          {renderLabel('Refill')}
          <Select
            name="refills"
            value={values.refills || 0}
            variant={selectType.SECONDARY}
            options={Array.of(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12).map(item => ({
              label: item,
              value: item,
            }))}
            onChange={event => {
              setFieldValue('refills', event.target.value);
            }}
          />
        </Box>
      </Box>
      <Box className={classes.section}>
        {renderLabel('Sig')}
        <Input
          fullWidth
          name="sig"
          value={values.sig}
          size={inputSize.M}
          placeholder="Enter Sig (Directions)"
          endAdornment={
            <Button
              disabled={isSigDisabled || sigLoading}
              size={btnSize.XSMALL}
              loading={sigLoading}
              variant={btnType.SECONDARY}
              onClick={handleSigGeneration}
            >
              Generate
            </Button>
          }
          onChange={event => setFieldValue('sig', event.target.value)}
          onBlur={setTouched('sig')}
        />
        <TextError errorMsg={touched.sig ? errors.sig?.toString() : null} />
      </Box>
      <Box className={classes.section}>
        {renderLabel('Dispense Amount')}
        <Input
          fullWidth
          name="dispenseAmount"
          value={values.dispenseAmount}
          size={inputSize.M}
          placeholder="Enter Dispense Amount"
          endAdornment={values.dose && values.dose !== '' ? values.dose : ''}
          onChange={event => {
            if (event.target.value === '') {
              return onChangeHandler('dispenseAmount', '');
            } else {
              if (Number(event.target.value) > 0) {
                return setFieldValue('dispenseAmount', Math.trunc(Number(event.target.value)));
              }
            }
          }}
          type="number"
          onBlur={setTouched('dispenseAmount')}
        />
        <TextError errorMsg={touched.dispenseAmount ? errors.dispenseAmount?.toString() : null} />
      </Box>
      <Box className={classes.section}>
        {renderLabel('Associated Diagnoses')}
        <Input
          fullWidth
          name="associatedDiagnoses"
          value={values.associatedDiagnoses}
          options={diagnosesOptions}
          variant={inputType.TAGS}
          size={inputSize.M}
          getOptionLabel={option => option}
          onBlur={setTouched('associatedDiagnoses')}
          onChange={async (_, newValue) => {
            await setFieldValue('associatedDiagnoses', newValue);
          }}
        />
      </Box>
      <Box className={classes.section}>
        {renderLabel('Prescribing Provider')}
        <Select
          name="provider"
          value={values.provider || 'Confidant Provider'}
          variant={selectType.SECONDARY}
          options={[{ label: 'Confidant Provider', value: 'Confidant Provider' }]}
          onChange={event => {
            setFieldValue('provider', event.target.value);
          }}
        />
        <TextError errorMsg={touched.provider ? errors.provider?.toString() : null} />
      </Box>
      <Box className={classes.section}>
        {renderLabel('Confidant Provider')}
        <Select
          name="confidantProvider"
          value={values.confidantProvider}
          variant={selectType.SECONDARY}
          options={providerListOptions?.sort((a, b) => a.label.localeCompare(b.label)) || []}
          emptyText="Select item"
          displayEmpty
          onChange={event => setFieldValue('confidantProvider', event.target.value)}
        />
        <TextError errorMsg={touched.confidantProvider ? errors.confidantProvider?.toString() : null} />
      </Box>
      <Box className={classes.section}>
        <FormControlLabel
          key={'allowSubstitutions'}
          checked={values.allowSubstitutions}
          name={'allowSubstitutions'}
          className={classes.toggle}
          onChange={() => setFieldValue('allowSubstitutions', !values.allowSubstitutions)}
          control={<Toggle />}
          label="Allow Substitutions"
        />
      </Box>
      <Box className={classes.section}>
        {renderLabel('Note to Pharmacy')}
        <Textarea
          name="pharmacyNotes"
          value={values.pharmacyNotes}
          placeholder="Enter note to pharmacy"
          onChange={event => setFieldValue('pharmacyNotes', event.target.value)}
          minRows={2}
          maxRows={3}
          onBlur={setTouched('pharmacyNotes')}
        />
        <TextError errorMsg={touched.pharmacyNotes ? errors.pharmacyNotes?.toString() : null} />
      </Box>
      <Box className={classes.section}>
        {renderLabel('Internal Note')}
        <Textarea
          name="additionalNotes"
          value={values.notes}
          placeholder="Enter your notes"
          onChange={event => setFieldValue('notes', event.target.value)}
          minRows={2}
          maxRows={3}
          onBlur={setTouched('notes')}
        />
        <TextError errorMsg={touched.notes ? errors.notes?.toString() : null} />
      </Box>
    </Box>
  );
};

export { MedicationForm };
